import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Search, Tag, Percent, Star, ExternalLink, Copy, Check, ChevronDown, ArrowLeft, Sparkles, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export default function ShopPage() {
  const { token, API, refreshUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [codes, setCodes] = useState([]);
  const [copiedCode, setCopiedCode] = useState('');
  const [purchasing, setPurchasing] = useState('');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shop/products`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { category, search, sort_by: sortBy }
      });
      setProducts(res.data.products);
      setCategories(res.data.categories || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token, category, search, sortBy]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openProduct = async (product) => {
    setSelectedProduct(product);
    try {
      const res = await axios.get(`${API}/shop/products/${product.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(res.data.codes || []);
    } catch (e) { setCodes([]); }
  };

  const handlePurchase = async (product) => {
    setPurchasing(product.id);
    try {
      const res = await axios.post(`${API}/shop/purchase?product_id=${product.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshUser();
      // Open affiliate link in new tab
      window.open(product.affiliate_url, '_blank');
    } catch (e) { console.error(e); }
    setPurchasing('');
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code.code);
      setCopiedCode(code.id);
      // Record code use
      await axios.post(`${API}/codes/use?code_id=${code.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshUser();
      setTimeout(() => setCopiedCode(''), 2000);
    } catch (e) { console.error(e); }
  };

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'cashback', label: 'Highest Cashback' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'newest', label: 'Newest' },
  ];

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <ShoppingBag className="w-5 h-5 text-amber-500" />
        <h2 className="font-heading font-bold text-base text-white">Shop & Earn</h2>
      </div>

      {/* Cashback Banner */}
      <div className="rounded-xl p-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
          <Percent className="w-4 h-4 text-amber-500" />
        </div>
        <div>
          <p className="text-xs font-bold text-amber-400">Up to 10% Cashback in Points</p>
          <p className="text-[10px] text-zinc-500">Shop through Honey Pot & earn on every purchase</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <Input
            data-testid="shop-search"
            placeholder="Search products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-[#121214] border-white/10 text-white text-xs pl-9 h-9 focus:border-amber-500/50"
          />
        </div>
        
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          <button
            data-testid="shop-cat-all"
            onClick={() => setCategory('')}
            className={`flex-shrink-0 px-3 py-1 rounded-full text-[10px] font-medium transition-all ${
              !category ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-zinc-500 border border-transparent hover:text-zinc-300'
            }`}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              data-testid={`shop-cat-${cat}`}
              onClick={() => setCategory(cat)}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-[10px] font-medium transition-all ${
                category === cat ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-zinc-500 border border-transparent hover:text-zinc-300'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-600">{products.length} products</span>
          <select
            data-testid="shop-sort"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="bg-[#121214] border border-white/10 text-zinc-400 text-[10px] rounded-md px-2 py-1 outline-none"
          >
            {sortOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12">
          <ShoppingBag className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">No products available yet</p>
          <p className="text-xs text-zinc-600 mt-1">Check back soon for deals!</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {products.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="glass-card rounded-xl overflow-hidden hover:border-amber-500/15 transition-all cursor-pointer"
              onClick={() => openProduct(p)}
              data-testid={`shop-product-${i}`}
            >
              <div className="p-3.5 flex gap-3">
                {/* Product Image Placeholder */}
                <div className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <ShoppingBag className="w-6 h-6 text-zinc-700" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-white truncate">{p.name}</p>
                      <p className="text-[10px] text-zinc-500">{p.store}</p>
                    </div>
                    <div className="flex-shrink-0 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                      <span className="text-[10px] font-bold text-emerald-400">{p.cashback_percent}%</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-sm font-bold font-mono text-white">${p.price}</span>
                    {p.original_price && p.original_price > p.price && (
                      <span className="text-[10px] text-zinc-600 line-through">${p.original_price}</span>
                    )}
                  </div>

                  {/* Best Creator Code */}
                  {p.best_code && (
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <Tag className="w-3 h-3 text-purple-400" />
                      <span className="text-[10px] text-purple-400 font-medium">
                        {p.best_code.creator_name}: {p.best_code.code}
                      </span>
                      {p.best_code.discount_value > 0 && (
                        <span className="text-[10px] text-zinc-600">
                          (-{p.best_code.discount_value}{p.best_code.discount_type === 'percentage' ? '%' : '$'})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {p.featured && (
                <div className="px-3.5 pb-2">
                  <div className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-amber-500" />
                    <span className="text-[9px] text-amber-500 font-medium">Featured Deal</span>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Product Detail Dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-sm max-h-[85vh] overflow-y-auto p-0">
          {selectedProduct && (
            <>
              {/* Product Header */}
              <div className="p-5 pb-0">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-16 h-16 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {selectedProduct.image_url ? (
                      <img src={selectedProduct.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ShoppingBag className="w-6 h-6 text-zinc-700" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-base text-white">{selectedProduct.name}</h3>
                    <p className="text-xs text-zinc-500">{selectedProduct.store}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-lg font-bold font-mono text-white">${selectedProduct.price}</span>
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-[10px] font-bold text-emerald-400">
                        {selectedProduct.cashback_percent}% back
                      </span>
                    </div>
                  </div>
                </div>

                {selectedProduct.description && (
                  <p className="text-xs text-zinc-400 leading-relaxed mb-4">{selectedProduct.description}</p>
                )}

                {/* Cashback Info */}
                <div className="glass-card rounded-lg p-3 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <Percent className="w-4 h-4 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xs text-white font-medium">
                      Earn <span className="text-amber-500 font-bold">{Math.max(1, Math.round(selectedProduct.price * selectedProduct.cashback_percent / 100))} points</span> cashback
                    </p>
                    <p className="text-[10px] text-zinc-500">When you purchase through Honey Pot</p>
                  </div>
                </div>

                {/* Purchase Button */}
                <button
                  data-testid="shop-buy-btn"
                  onClick={() => handlePurchase(selectedProduct)}
                  disabled={purchasing === selectedProduct.id}
                  className="honey-btn w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 mb-4"
                >
                  <ExternalLink className="w-4 h-4" />
                  {purchasing === selectedProduct.id ? 'Opening...' : 'Shop & Earn Cashback'}
                </button>
              </div>

              {/* Creator Codes Section */}
              {codes.length > 0 && (
                <div className="border-t border-white/5 p-5">
                  <p className="text-xs font-bold text-zinc-400 mb-3 flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" />
                    Creator Codes for {selectedProduct.store}
                  </p>
                  <div className="space-y-2">
                    {codes.map((code, i) => (
                      <div
                        key={code.id}
                        className="flex items-center gap-3 glass-card rounded-lg px-3 py-2.5"
                        data-testid={`product-code-${i}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono font-bold text-white bg-white/5 px-2 py-0.5 rounded">
                              {code.code}
                            </span>
                            {code.verified && (
                              <ShieldCheck className="w-3 h-3 text-emerald-400" />
                            )}
                          </div>
                          <p className="text-[10px] text-zinc-500 mt-0.5">
                            by {code.creator_name}
                            {code.discount_value > 0 && (
                              <span className="text-zinc-400"> &middot; {code.discount_value}{code.discount_type === 'percentage' ? '%' : '$'} off</span>
                            )}
                          </p>
                        </div>
                        <button
                          data-testid={`copy-code-${i}`}
                          onClick={(e) => { e.stopPropagation(); copyCode(code); }}
                          className="px-2.5 py-1 rounded-md bg-white/5 text-xs text-zinc-400 hover:text-white hover:bg-white/10 transition-colors flex items-center gap-1"
                        >
                          {copiedCode === code.id ? (
                            <><Check className="w-3 h-3 text-emerald-400" /><span className="text-emerald-400">Copied</span></>
                          ) : (
                            <><Copy className="w-3 h-3" /><span>Copy</span></>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
